import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripePaymentMethodType } from "@/services/stripe-subs-payments.service";
import { stripePromise, stripePublishableKey } from "@/lib/stripe";

interface StripePaymentModalProps {
  open: boolean;
  clientSecret: string | null;
  paymentIntentId: string | null;
  amount: number;
  currency: string | null;
  applicationFeeAmount: number;
  netTransferAmount: number;
  paymentMethodType: StripePaymentMethodType | null;
  onCancel: () => void;
  onSuccess: (paymentIntentId: string) => void;
}

function formatAmount(amount: number, currency: string | null) {
  const resolvedCurrency = currency ? currency.toUpperCase() : "USD";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: resolvedCurrency,
    minimumFractionDigits: 2,
  }).format(amount / 100);
}

function formatPaymentMethod(paymentMethodType: StripePaymentMethodType | null) {
  if (paymentMethodType === "card") return "Card";
  if (paymentMethodType === "us_bank_account") return "Bank account (ACH)";
  return "Selected method";
}

function StripePaymentForm({
  amount,
  currency,
  paymentIntentId,
  onCancel,
  onSuccess,
}: {
  amount: number;
  currency: string | null;
  paymentIntentId: string | null;
  onCancel: () => void;
  onSuccess: (paymentIntentId: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const formattedAmount = useMemo(() => formatAmount(amount, currency), [amount, currency]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements || !paymentElementReady) {
      setErrorMessage("Stripe is still loading. Please try again in a moment.");
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      if (result.error) {
        setErrorMessage(result.error.message ?? "Unable to complete payment. Please try again.");
        setSubmitting(false);
        return;
      }

      const paymentIntent = result.paymentIntent;
      if (paymentIntent?.status === "succeeded") {
        onSuccess(paymentIntent.id);
        return;
      }

      // ACH payments enter "processing" state — takes 4-5 business days to settle
      if (paymentIntent?.status === "processing") {
        onSuccess(paymentIntent.id);
        return;
      }

      setErrorMessage(`Payment status: ${paymentIntent?.status ?? "unknown"}`);
      setSubmitting(false);
    } catch (error: any) {
      setErrorMessage(error?.message ?? "Unable to complete payment. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{ layout: { type: "tabs", defaultCollapsed: false } }}
        onReady={() => setPaymentElementReady(true)}
      />
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !stripe || !elements || !paymentElementReady}>
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {submitting ? "Processing…" : `Pay ${formattedAmount}`}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function StripePaymentModal({
  open,
  clientSecret,
  paymentIntentId,
  amount,
  currency,
  applicationFeeAmount,
  netTransferAmount,
  paymentMethodType,
  onCancel,
  onSuccess,
}: StripePaymentModalProps) {
  if (!stripePromise || !stripePublishableKey) {
    console.warn("Stripe publishable key is missing. Stripe Payment modal will not render.");
    return null;
  }

  if (!clientSecret) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(value) => (!value ? onCancel() : undefined)}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Complete Stripe Payment</DialogTitle>
          <DialogDescription>Finish the payment securely with Stripe.</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Payment method</span>
            <span className="font-medium">{formatPaymentMethod(paymentMethodType)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Gross payment</span>
            <span className="font-medium">{formatAmount(amount, currency)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Estimated Stripe fee withheld</span>
            <span className="font-medium">{formatAmount(applicationFeeAmount, currency)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Estimated recipient net</span>
            <span className="font-medium">{formatAmount(netTransferAmount, currency)}</span>
          </div>
        </div>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: "stripe",
            },
          }}
          key={clientSecret}
        >
          <StripePaymentForm amount={amount} currency={currency} paymentIntentId={paymentIntentId} onCancel={onCancel} onSuccess={onSuccess} />
        </Elements>
      </DialogContent>
    </Dialog>
  );
}
